import { Button, Card, Column, Grid, IconButton, Row, Skeleton } from 'components/Blocks';
import { Coin } from 'components/DecorativeIcons';

import { loadActions } from 'entities';

import { RefreshIcon } from 'components/Blocks/Icons';
import { SiteTextLogo } from 'components/Logos';

import { useLang } from 'locale';

import { useSurveys } from './surveys';
import { OnboardingModal } from './OnboardingModal';
import { cn } from 'styles';

interface SurveyListProps {
  allowRefresh?: boolean;
  oneOrNone?: boolean;
}

const SURVEYS_TO_SHOW = 6;

export function SurveyList({ allowRefresh = true, oneOrNone }: SurveyListProps) {
  const { t } = useLang();

  const { availableSurveys, onSurveyClick, overwriteSurveys, isLoadingSurveys } = useSurveys();

  const loadSurveys = loadActions.surveys.eligibleSurveys.use({
    onSuccess: overwriteSurveys,
  });

  return (
    <>
      <OnboardingModal onSurveyClick={onSurveyClick} />

      <Column fullWidth fullHeight>
        <SiteTextLogo className="mx-auto mb-2 mt-4 small_tablet:hidden" />

        {allowRefresh && !oneOrNone && (
          <Row justifyContent="center" className="mb-3">
            <h3 className="text-xl font-semibold text-foreground-title">
              {t('available-surveys')}
            </h3>

            <IconButton
              className="group bg-hover text-primary hover:bg-primary hover:text-primary-foreground"
              loading={loadSurveys.loading}
              onClick={loadSurveys.execute}
            >
              <RefreshIcon
                size={16}
                className="transition-transform duration-500 group-hover:-rotate-90"
              />
            </IconButton>
          </Row>
        )}

        <Grid
          gap="4"
          cols={3}
          className="mx-auto w-full max-w-[1460px] tablet:grid-cols-2 phone:grid-cols-1"
        >
          {loadSurveys.loading || isLoadingSurveys ? (
            <Skeleton
              quantity={SURVEYS_TO_SHOW}
              wrapMultiple={false}
              height={180}
              className="bg-primary/10"
            />
          ) : (
            <>
              {availableSurveys
                .slice(0, oneOrNone ? 1 : SURVEYS_TO_SHOW)
                .map(({ url, id, incentives, lois }) => (
                  <Card
                    flow="column"
                    key={id}
                    alignItems="center"
                    className={cn(
                      'shadow-main transition-colors has-[a:hover]:border-primary',
                      oneOrNone && 'mx-auto w-full max-w-[80vw] border-primary/40',
                    )}
                    gap="5"
                  >
                    <span className="text-sm font-medium text-foreground-light">
                      {t('survey-number', { id })}
                    </span>

                    <Row>
                      <Coin />

                      <Column gap="0.5">
                        <strong className="text-xl font-semibold">
                          {incentives} {t('points')}
                        </strong>
                        <span className="text-sm text-foreground-light">
                          {lois} {t('minutes')}
                        </span>
                      </Column>
                    </Row>

                    <Button
                      href={url}
                      linkTarget={id}
                      className="w-full"
                      onClick={() => onSurveyClick(id)}
                    >
                      {t('answer')}
                    </Button>
                  </Card>
                ))}
            </>
          )}
        </Grid>

        {!loadSurveys.loading && !availableSurveys.length && (
          <p className="mx-auto max-w-[65ch] text-center text-lg font-medium text-foreground">
            {t('no-surveys-atm')}
          </p>
        )}
      </Column>
    </>
  );
}
